'use client';

import toast from 'react-hot-toast';
import {
  RECORDING_AUTO_RESTART_OPT_OUT_LABEL,
  RECORDING_RECOVER_BUSY_MESSAGE,
  RECORDING_RECOVER_BUTTON_LABEL,
  RECORDING_RECOVER_MISS_MESSAGE,
  RECORDING_RECOVER_OK_MESSAGE,
  RECORDING_RECOVER_QUALITY_MESSAGE,
  RECORDING_RETRY_BUTTON_LABEL,
} from '@/lib/recording-ui-messages';
import { postAutoRestartOptOut, postRecoverRecording } from '@/lib/recording-status-client';

type RecordingFailureToastProps = {
  toastId: string;
  message: string;
  roomName?: string;
  onRetry?: () => void;
};

export function showRecordingFailureToast({
  message,
  roomName,
  onRetry,
}: Omit<RecordingFailureToastProps, 'toastId'>) {
  toast.custom(
    (t) => (
      <div
        className="rounded-lg border border-red-200 bg-white px-4 py-3 shadow-lg"
        style={{ maxWidth: 420 }}
      >
        <p className="text-sm text-gray-900 mb-3">{message}</p>
        <div className="flex flex-wrap gap-2">
          {onRetry ? (
            <button
              type="button"
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              onClick={() => {
                onRetry();
                toast.dismiss(t.id);
              }}
            >
              {RECORDING_RETRY_BUTTON_LABEL}
            </button>
          ) : null}
          {roomName ? (
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => {
                void postAutoRestartOptOut(roomName);
                toast.dismiss(t.id);
              }}
            >
              {RECORDING_AUTO_RESTART_OPT_OUT_LABEL}
            </button>
          ) : null}
          {roomName ? (
            <button
              type="button"
              className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              onClick={() => {
                void postRecoverRecording(roomName).then((result) => {
                  toast.dismiss(t.id);
                  if (result.synced) {
                    toast.success(RECORDING_RECOVER_OK_MESSAGE, { icon: '🎬' });
                    return;
                  }
                  if (result.error === 'recording_in_progress') {
                    toast.error(RECORDING_RECOVER_BUSY_MESSAGE);
                    return;
                  }
                  if (result.recovered) {
                    toast(RECORDING_RECOVER_QUALITY_MESSAGE);
                    return;
                  }
                  toast(RECORDING_RECOVER_MISS_MESSAGE);
                });
              }}
            >
              {RECORDING_RECOVER_BUTTON_LABEL}
            </button>
          ) : null}
        </div>
      </div>
    ),
    { duration: 20_000, id: 'recording-failed' },
  );
}
